import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BodyMigrateRSCTk3dCore } from '../models/contentdistributionAPI.model';
import { CdVenueModel } from '../entities/venues/models/venue.model';
import { GenericResponse } from '../../../shared/interfaces/response.interface';

export interface VenueExportToCSVResponse {
  id: number;
  configId: string;
  regions: string;
  venueName: string;
}


@Injectable({
  providedIn: 'root',
})
export class ContentDistributionApiService {
  private apiRoot = '/api';

  constructor(private http: HttpClient) {}

  // CRUD standard

  public getAll(
    entity: string,
    page?: number,
    order?: string,
    direction?: string,
    paginated?: boolean
  ): Observable<any> {
    let params = new HttpParams();
    const queryParams: Record<string, string | number | boolean> = { page, order, direction, paginated };
    params = Object.entries(queryParams).reduce(
      (acc, [key, value]) => (value !== undefined && value !== null ? acc.set(key, value.toString()) : acc),
      params
    );
    const endpoint = `${environment.contentdistribution.private.url}${this.apiRoot}/${entity}`;
    return this.http.get<any>(endpoint, { params });
  }

  public create(entity: string, body: any): Observable<any> {
    return this.http.post<any>(environment.contentdistribution.private.url + `${this.apiRoot}/${entity}`, body);
  }

  //! No se usa?
  public update(entity: string, id: number, body: any): Observable<any> {
    return this.http.patch<any>(environment.contentdistribution.private.url + `${this.apiRoot}/${entity}/${id}`, body);
  }

  public delete(entity: string, id: number): Observable<any> {
    return this.http.delete<any>(environment.contentdistribution.private.url + `${this.apiRoot}/${entity}/${id}`);
  }

  // Solo se usa para clients
  public deletebykey(entity: string, key: string): Observable<any> {
    return this.http.delete<any>(environment.contentdistribution.private.url + `${this.apiRoot}/${entity}/key/${key}`);
  }

  // Get by
  public getById(entity: string, id: number) {
    return this.http.get<any>(environment.contentdistribution.private.url + `${this.apiRoot}/${entity}/${id}`);
  }

  // Solo se usa para clients
  public getByKey(entity: string, key: string) {
    return this.http.get<any>(environment.contentdistribution.private.url + `${this.apiRoot}/${entity}/key/${key}`);
  }

  // Client Venues
  public getClientVenues(key: string, page: number = 1, code?: string): Observable<any> {
    let params = new HttpParams();
    params = params.set('key', key);
    params = params.set('page', page);
    if(code){
      params = params.set('code', code);
    }
    return this.http.get<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/clients/key/${key}/venues`, { params }
    );
  }

  // All Client Venues
  public getAllClientVenues(key: string): Observable<any> {
    return this.http.get<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/clients/key/${key}/venues/all`
    );
  }

  // Client Venues
  public updateClientVenues(id: number, overwrite: boolean, body: { venues: number[] }): Observable<any> {
    return this.http.patch<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/clients/${id}/venues?overwrite=${overwrite}`,
      body
    );
  }

  // Client Venues
  public deleteClientVenues(id: number, overwrite: boolean, body: { venues: number[] }): Observable<any> {
    return this.http.delete<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/clients/${id}/venues?overwrite=${overwrite}`,
      { body: body }
    );
  }

  // Venues
  public getAllVenues() {
    return this.http.get<any>(environment.contentdistribution.private.url + `${this.apiRoot}/venues/listall/keys`);
  }

  public getClientsByCode(code: string) {
    return this.http.get<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/venues/code/${code}/clients`
    );
  }

  public getVenuesByCodes(body: { codes: string[] }, fields?: string, related?: string) {
    let query: string = '';
    if (fields && related) query = `?fields=${fields}&related=${related}`;
    else if (fields && !related) query = `?fields=${fields}`;
    else if (!fields && related) query = `?related=${related}`;
    return this.http.post<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/venues/code${query}`,
      body
    );
  }

  public getVenuesByKey(venueKey, fields) {
    let query = '';
    if (fields && fields.length) {
      query = `?fields=${fields.join()}`;
    }
    return this.http.get<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/venues/key/${venueKey}${query}`
    );
  }

  public getCodesbyVenues(keys: string[]) {
    return this.http.post<any>(environment.contentdistribution.private.url + `${this.apiRoot}/venues/key/code`, {
      keys,
    });
  }

  public getVenuesByVkeys(keys: string[], related?: string, fields?: string): Observable<CdVenueModel[] | any> {
    let params = new HttpParams();
    params = fields ? params.set('fields', fields) : params.set('fields', 'key,has360');
    params = related ? params.set('related', related) : params;
    const url = `${environment.contentdistribution.private.url}${this.apiRoot}/venues/key`;
    const body = { keys };
    return this.http.post<any>(url, body, { params });
  }

  public migrateSingleResource(body: BodyMigrateRSCTk3dCore, venuecode: string) {
    // Example
    // POST /migrate/resources
    /*     {
          "source":{
              "bucket":"panoresourcestk3d-us-west-1",
              "key":"nam-ca-10009-brucespringsteen23-sec/maps/blockmap2d/master_full.json"
          },
          "destination":[
              {
              "bucket": "rsc-contentdistribution-eu-west-1.3ddvapis.com",
              "keys": ["nam-ca-10009-brucespringsteen23-sec/maps/blockmap2d/master_full.json"]
              }
          ]
      } */
    return this.http.post<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/venues/${venuecode}/migrate/singleresource`,
      body
    );
  }

  public migrateAllResource(body: BodyMigrateRSCTk3dCore, venuecode: string) {
    // Example
    // POST /migrate/resources
    /*
        {
            "source":{
                "bucket":"panoresourcestk3d-us-west-1",
                "key":"nam-ca-10009-brucespringsteen23-sec"
            },
            "destination":
                {
                "clients": ["CLIENTKEY1","CLIENTKEY2"]
                }

        }
      */
    return this.http.post<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/venues/${venuecode}/migrate/resources`,
      body
    );
  }

  public updateVenueMaps(id, venue) {
    const body = { venues: venue };
    return this.http.patch<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/venues/${id}/maps/bulk`,
      body
    );
  }

  public getClientVenuesByCode(key, codes: string[]) {
    const body = { codes: codes };
    return this.http.post<any>(
      environment.contentdistribution.private.url + `${this.apiRoot}/clients/key/${key}/venues/exists`,
      body
    );
  }

  // Domains
  public createDomainsBulk(data: Array<{ name: string; clientId: number }>) {
    const body = { data };
    return this.http.post<any>(environment.contentdistribution.private.url + `${this.apiRoot}/domains/bulk`, body);
  }

  public getVenuesToExportCSV(): Observable<VenueExportToCSVResponse[]> {
    const endpoint = environment.contentdistribution.private.url + `${this.apiRoot}/admin/config/export-to-csv`
    return this.http.get<GenericResponse<VenueExportToCSVResponse[]>>(endpoint).pipe(
      map(response => response.data)
    );
  }
}
